"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { useBranchFilter } from "@/hooks/use-branch-filter";
import { differenceInDays, format, parseISO } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { useLocalization } from "@/context/localization-context";

interface TrendQualityScoreProps {
  className?: string;
}

export function TrendQualityScore({ className }: TrendQualityScoreProps) {
  const { processedDateRange } = useDashboardDateRange();
  const { start, end } = processedDateRange;
  const branchFilter = useBranchFilter();
  const { locale, t, tp, formatNumber } = useLocalization();

  const [data, setData] = useState<
    { dateStr: string; label: string; score: number }[]
  >([]);
  const { mutateAsync, isPending } = usePostAggregatePizzas({});

  const daysCount = start && end ? differenceInDays(end, start) + 1 : 7;
  const titleText = tp("trend.title", daysCount, {
    count: formatNumber(daysCount),
  });

  useEffect(() => {
    if (!start || !end) return;

    let isMounted = true;
    const fetchData = async () => {
      try {
        const response = await mutateAsync({
          data: {
            pipeline: [
              {
                $match: {
                  ...branchFilter,
                  "evaluation.metadata.classification": { $exists: true },
                  capture_at: {
                    $gte: start.toISOString(),
                    $lte: end.toISOString(),
                  },
                },
              },
              {
                $project: {
                  capture_at: 1,
                  dateStr: {
                    $dateToString: {
                      format: "%Y-%m-%d",
                      date: { $toDate: "$capture_at" },
                    },
                  },
                  classification: "$evaluation.metadata.classification",
                },
              },
              {
                $project: {
                  dateStr: 1,
                  evaluatable: {
                    $cond: [
                      {
                        $and: [
                          { $ne: ["$classification", "No Pizza"] },
                          { $ne: [{ $type: "$classification" }, "missing"] },
                          { $ne: ["$classification", null] },
                        ],
                      },
                      1,
                      0,
                    ],
                  },
                  ok: {
                    $cond: [{ $eq: ["$classification", "OK Pizza"] }, 1, 0],
                  },
                },
              },
              {
                $group: {
                  _id: "$dateStr",
                  total: { $sum: "$evaluatable" },
                  ok: { $sum: "$ok" },
                },
              },
              { $sort: { _id: 1 } },
            ],
          },
        });

        if (!isMounted) return;

        const rawResults = (response as { results?: unknown[] }).results ?? [];
        const dateLocale = locale === "cs" ? cs : enUS;
        const formattedData = rawResults
          .map((row) => {
            const result = row as Record<string, unknown>;
            const total = typeof result.total === "number" ? result.total : 0;
            const ok = typeof result.ok === "number" ? result.ok : 0;
            const dateStr = typeof result._id === "string" ? result._id : "";
            const score = total > 0 ? (ok / total) * 100 : 0;

            let label = dateStr;
            try {
              label = format(parseISO(dateStr), "EEEEEE", {
                locale: dateLocale,
              });
              label = label.charAt(0).toUpperCase() + label.slice(1);
            } catch {
              // keep original date string
            }

            return {
              dateStr,
              label,
              score,
            };
          })
          .filter((row) => row.score > 0);

        setData(formattedData);
      } catch (error) {
        console.error("Error fetching trend scores:", error);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [mutateAsync, start, end, branchFilter, locale]);

  const getBarColor = (score: number) => {
    if (score >= 84) return "bg-[#4d7c1e]";
    return "bg-[#c57d19]";
  };

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base font-medium uppercase tracking-wider text-muted-foreground">
          {titleText}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="flex h-[200px] items-center justify-center py-4 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-[200px] items-center justify-center py-4 text-center text-sm text-muted-foreground">
            {t("common.noPeriodData")}
          </div>
        ) : (
          <div className="flex h-[200px] w-full items-end justify-between gap-2 pt-6">
            {data.map((item) => (
              <div
                key={item.dateStr}
                className="group relative flex h-full flex-1 flex-col items-center justify-end"
              >
                <div className="mb-2 whitespace-nowrap text-xs font-medium text-muted-foreground">
                  {formatNumber(Math.round(item.score) / 100, {
                    style: "percent",
                  })}
                </div>
                <div
                  className={cn(
                    "w-full rounded-sm transition-all duration-300",
                    getBarColor(item.score),
                  )}
                  style={{ height: `${Math.max(item.score * 0.8, 10)}%` }}
                />
                <div className="mt-2 text-xs text-muted-foreground">
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
